import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import axios from 'axios';

const ParentalConsentResponsePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const action = searchParams.get('action');
  const rawName = searchParams.get('name');
  const childName = rawName
    ? rawName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
    : null;

  const [status, setStatus] = useState<'loading' | 'approved' | 'declined' | 'already_responded' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [alreadyRespondedStatus, setAlreadyRespondedStatus] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps -- token and action come from URL params which don't change after mount
  useEffect(() => {
    if (!token || !action) {
      setStatus('error');
      setErrorMessage('Invalid or missing consent link. Please check the email and try again.');
      return;
    }

    axios
      .get(`api/parental-consent/respond/${token}?action=${action}`)
      .then(() => {
        setStatus(action === 'approve' ? 'approved' : 'declined');
      })
      .catch(err => {
        const data = err?.response?.data;
        if (data?.error === 'already_responded') {
          setAlreadyRespondedStatus(data.status || '');
          setStatus('already_responded');
        } else {
          setErrorMessage(data?.message || 'Something went wrong. Please try again or contact support@reachamy.com');
          setStatus('error');
        }
      });
  }, []);

  const containerStyle = { height: '75vh' };

  if (status === 'loading') {
    return (
      <Box className="d-flex align-items-center justify-content-center" sx={containerStyle}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'approved') {
    return (
      <Box className="d-flex align-items-center justify-content-center" sx={containerStyle}>
        <Box className="text-center" sx={{ maxWidth: '700px', px: 3 }}>
          <img src="content/images/logos/reachamy-logo.webp" alt="ReachAmy" style={{ height: '100px', marginBottom: '16px' }} />
          <Typography variant="h4" gutterBottom sx={{ color: '#28a745', fontWeight: 700 }}>
            Consent Approved!
          </Typography>
          <Typography variant="body1">
            Thank you for approving{childName ? ` ${childName}'s` : ''} account on ReachAMY.
          </Typography>
          <Typography variant="body1">
            {childName || 'Your child'} can now log in and explore volunteering opportunities.
          </Typography>
          <Button
            variant="contained"
            sx={{
              mt: 3,
              fontSize: '14px',
              borderRadius: '20px',
              height: '28px',
              lineHeight: '12px',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'primary.main', color: 'common.black' },
              width: 'fit-content',
            }}
            onClick={() => navigate('/')}
          >
            Go to ReachAMY
          </Button>
        </Box>
      </Box>
    );
  }

  if (status === 'declined') {
    return (
      <Box className="d-flex align-items-center justify-content-center" sx={containerStyle}>
        <Box className="text-center" sx={{ maxWidth: '800px', px: 3 }}>
          <img src="content/images/logos/reachamy-logo.webp" alt="ReachAmy" style={{ height: '100px', marginBottom: '16px' }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Access Declined
          </Typography>
          <Typography variant="body1" gutterBottom>
            You have declined{childName ? ` ${childName}'s` : ''} access to ReachAMY.
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'nowrap' }}>
            {childName || 'Your child'} will be notified. If this was a mistake, please contact us at{' '}
            <a href="mailto:support@reachamy.com" style={{ color: 'inherit' }}>support@reachamy.com</a>.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (status === 'already_responded') {
    const isExpired = alreadyRespondedStatus === 'EXPIRED';
    const wasApproved = alreadyRespondedStatus === 'APPROVED';
    return (
      <Box className="d-flex align-items-center justify-content-center" sx={containerStyle}>
        <Box className="text-center" sx={{ maxWidth: '800px', px: 3 }}>
          <img src="content/images/logos/reachamy-logo.webp" alt="ReachAmy" style={{ height: '100px', marginBottom: '16px' }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            {isExpired ? 'Link Expired' : 'Already Responded'}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {isExpired
              ? 'This consent link has expired. The 7-day window has passed without a response.'
              : `You have already ${wasApproved ? 'approved' : 'declined'} this consent request.`}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'nowrap' }}>
            {isExpired
              ? `${childName || 'Your child'} can log in to the ReachAmy app and send a new consent request.`
              : 'This link has already been used and cannot be used again.'}{' '}
            For any questions, contact us at
            <br />
            <a href="mailto:support@reachamy.com" style={{ color: 'inherit' }}>support@reachamy.com</a>.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="d-flex align-items-center justify-content-center" sx={containerStyle}>
      <Box className="text-center" sx={{ maxWidth: '480px', px: 3 }}>
        <img src="content/images/logos/reachamy-logo.webp" alt="ReachAmy" style={{ height: '58px', marginBottom: '24px' }} />
        <Typography variant="h4" gutterBottom sx={{ color: 'error.main', fontWeight: 700 }}>
          Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {errorMessage}
        </Typography>
      </Box>
    </Box>
  );
};

export default ParentalConsentResponsePage;
